import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const FooterContainer = styled.footer`
  background-color: #000000;
  color: var(--color-light-gray);
  padding: var(--spacing-xl) 0;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--spacing-lg);
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterLogo = styled.div`
  font-family: var(--font-primary);
  font-size: 1.8rem;
  font-weight: var(--font-weight-light);
  margin-bottom: var(--spacing-md);
  display: flex;
  align-items: center;
`;

const SecondaryLogo = styled.img`
  height: 24px;
  width: auto;
  margin-left: 15px;
`;

const FooterText = styled.p`
  margin-bottom: var(--spacing-sm);
  font-size: 0.9rem;
  opacity: 0.9;
`;

const FooterHeading = styled.h4`
  font-size: 1.1rem;
  margin-bottom: var(--spacing-md);
  font-weight: var(--font-weight-light);
`;

const FooterLink = styled(Link)`
  color: var(--color-background);
  margin-bottom: var(--spacing-xs);
  font-size: 0.9rem;
  opacity: 0.9;
  
  &:hover {
    opacity: 1;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: var(--spacing-md);
  margin-top: var(--spacing-md);
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  background-color: transparent;
  color: var(--color-light-gray);
  transition: all 0.3s ease;
  
  &:hover {
    border-color: var(--color-background);
    color: var(--color-background);
    transform: translateY(-2px);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Copyright = styled.div`
  text-align: center;
  margin-top: var(--spacing-xl);
  padding-top: var(--spacing-md);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.8rem;
  opacity: 0.8;
`;

const QuoteContainer = styled.div`
  text-align: center;
  margin-top: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
  font-family: var(--font-primary);
  font-style: italic;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  padding: 0 var(--spacing-md);
  
  &::before,
  &::after {
    content: '';
    height: 1px;
    width: 50px;
    background-color: rgba(255, 255, 255, 0.2);
    position: absolute;
    top: 50%;
  }
  
  &::before {
    left: 0;
  }
  
  &::after {
    right: 0;
  }
  
  @media (max-width: 768px) {
    &::before,
    &::after {
      display: none;
    }
  }
`;

const Quote = styled.blockquote`
  font-size: 1.3rem;
  font-style: italic;
  font-weight: bold;
  line-height: 1.6;
  margin-bottom: var(--spacing-xs);
  letter-spacing: 0.5px;
  color: var(--color-background);
  opacity: 0.9;
  
  &::before,
  &::after {
    content: '';
    display: inline-block;
    width: 20px;
  }
`;

const QuoteAuthor = styled.cite`
  font-size: 0.9rem;
  font-style: bold;
  opacity: 0.7;
  display: block;
  margin-top: var(--spacing-sm);
  letter-spacing: 1px;
`;

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <FooterContainer>
      <div className="container">
      <QuoteContainer>
          <Quote>« J'aime le beau et l'utile, et plus encore le beau dans l'utile. »</Quote>
          <QuoteAuthor>— Andrée Putman</QuoteAuthor>
        </QuoteContainer>
        <FooterContent>
          <FooterColumn>
            <FooterLogo>
              Agence Intérieurs
              <SecondaryLogo 
                src="https://supabase.hkcom.fr/storage/v1/object/public/agence-interieurs/logo/logofbdep.png" 
                alt="Logo FBDP" 
              />
            </FooterLogo>
            <FooterText>
              Nous créons des espaces intérieurs élégants, fonctionnels pour des projets résidentiels et commerciaux.
            </FooterText>
            <SocialLinks>
              <SocialLink href="https://www.instagram.com/fabienneboedepirey/" target="_blank" rel="noopener noreferrer">
                <svg viewBox="0 0 448 512" fill="currentColor">
                  <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"></path>
                </svg>
              </SocialLink>
              <SocialLink href="https://facebook.com" target="_blank" rel="noopener noreferrer">
                <svg viewBox="0 0 320 512" fill="currentColor">
                  <path d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z"></path>
                </svg>
              </SocialLink>
              <SocialLink href="https://www.pinterest.fr/fabienneboedepirey/" target="_blank" rel="noopener noreferrer">
                <svg viewBox="0 0 384 512" fill="currentColor">
                  <path d="M204 6.5C101.4 6.5 0 74.9 0 185.6 0 256 39.6 296 63.6 296c9.9 0 15.6-27.6 15.6-35.4 0-9.3-23.7-29.1-23.7-67.8 0-80.4 61.2-137.4 140.4-137.4 68.1 0 118.5 38.7 118.5 109.8 0 53.1-21.3 152.7-90.3 152.7-24.9 0-46.2-18-46.2-43.8 0-37.8 26.4-74.4 26.4-113.4 0-66.2-93.9-54.2-93.9 25.8 0 16.8 2.1 35.4 9.6 50.7-13.8 59.4-42 147.9-42 209.1 0 18.9 2.7 37.5 4.5 56.4 3.4 3.8 1.7 3.4 6.9 1.5 50.4-69 48.6-82.5 71.4-172.8 12.3 23.4 44.1 36 69.3 36 106.2 0 153.9-103.5 153.9-196.8C384 71.3 298.2 6.5 204 6.5z"></path>
                </svg>
              </SocialLink>
              <SocialLink href="https://www.linkedin.com/in/fabienne-bo%C3%A9-de-pirey-92a17519/" target="_blank" rel="noopener noreferrer">
                <svg viewBox="0 0 448 512" fill="currentColor">
                  <path d="M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1 107.58 0c0 29.7-24.1 54.3-53.79 54.3zM447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2-48.29 0-55.69 37.7-55.69 76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.88-48.3 94 0 111.28 61.9 111.28 142.3V448z"></path>
                </svg>
              </SocialLink>
            </SocialLinks>
          </FooterColumn>
          
          <FooterColumn>
            <FooterHeading>Services</FooterHeading>
            <FooterLink to="/services">Rénovation de lieux publics ou privés</FooterLink>
            <FooterLink to="/services">Architecture d'intérieur</FooterLink>
            <FooterLink to="/services">Décoration d'intérieur</FooterLink>
            <FooterLink to="/services">Conseils en aménagement</FooterLink>
            <FooterLink to="/services">Conception de mobilier</FooterLink>
          </FooterColumn>
          
          <FooterColumn>
            <FooterHeading>Contact</FooterHeading>
            <FooterText>23 rue Octave Feuillet</FooterText>
            <FooterText>75116 Paris</FooterText>
            <FooterText>fabienne@agence-interieurs.fr</FooterText>
            <FooterText>+33.(0)6.60.73.24.01</FooterText>
          </FooterColumn>
        </FooterContent>
        
        <Copyright>
          {currentYear} Agence Intérieurs - Fabienne Boé de Pirey. Tous droits réservés. | Développé par{' '}
          <a href="https://hkcom.fr" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>HKCOM.fr</a>
          <br />
          Crédit photos : @romainboé
        </Copyright>
      </div>
    </FooterContainer>
  );
};

export default Footer;
